import {
  getLoadAgents,
  getLoadStatuses,
  getEPOStatuses,
  getNewLoad,
  getCustomersOrTraders,
  getEPOSiteNames,
  postEPOMovementDirection,
} from '../apiCalls';

const TYPE_TO_SEARCH_DELAY = 3000;

export const fetchDropdownOptions = async (isEPO, isEORIUser) => {
  // TODO: could consider putting these in a Promise.all - check with BE if this might strain the DB?
  let statuses = isEPO ? await getEPOStatuses() : await getLoadStatuses();
  const agents = !isEPO ? await getLoadAgents() : [];
  const routes = isEPO ? await postEPOMovementDirection() : await getNewLoad('movement');
  const siteNames = isEPO && isEORIUser ? await getEPOSiteNames() : [];

  if (isEPO) {
    // filter out 'Submitted' status for EPOs
    statuses = statuses.filter((opt) => opt.status !== 'Submitted');
  }

  // options should be array of objects with id and data fields
  return {
    statuses: statuses.length ? statuses.map((opt) => ({ id: opt.statusNumber || opt.status, data: opt.status })) : [],
    agents: agents.length ? agents.map((opt) => ({ id: opt.fullname, data: opt.fullname })) : [],
    routes: routes.length
      ? routes.map((opt) => ({ id: opt.data || opt.movementType, data: opt.data || opt.movementType }))
      : [],
    customerNames: [],
    customerEoris: [],
    siteNames: siteNames.length ? siteNames.map((opt) => ({ id: opt.siteName, data: opt.siteName })) : [],
    epoTypes: [
      { id: 0, data: 'Standard' },
      { id: 1, data: 'Priority' },
      { id: 2, data: 'Priority (TAD)' },
    ],
  };
};

export const getTypeToSearchTimeout = (searchTerm, field, setOptions, setIsLoading) => {
  const searchingByName = field === 'name';
  const searchingByEori = field === 'eori';

  const newTimeout = setTimeout(async () => {
    const data = await getCustomersOrTraders(searchingByName ? searchTerm : '', searchingByEori ? searchTerm : '', 0);

    setOptions((prev) => ({
      ...prev,
      ...(searchingByName && {
        customerNames: data.length ? data.map((opt) => ({ id: opt.companyName, data: opt.companyName })) : [],
      }),
      ...(searchingByEori && {
        customerEoris: data.length ? data.map((opt) => ({ id: opt.companyEori, data: opt.companyEori })) : [],
      }),
    }));

    setIsLoading(false);
  }, TYPE_TO_SEARCH_DELAY);

  return newTimeout;
};

export const getMaxDate = (date) => {
  if (!date) return null;

  // returns date plus 6 months in the format yyyy-mm-dd
  const d = new Date(date);
  d.setMonth(d.getMonth() + 6);
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

export const getMinDate = (date) => {
  if (!date) return null;

  // returns date minus 6 months in the format yyyy-mm-dd
  const d = new Date(date);
  d.setMonth(d.getMonth() - 6);
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

export const getDefaultCreatedDates = () => {
  const now = new Date();
  const defaultCreatedTo = new Date(now.getFullYear(), now.getMonth() + 1, 0, 12).toISOString().split('T')[0];
  const defaultCreatedFrom = new Date(now.getFullYear(), now.getMonth() - 2, 1, 12).toISOString().split('T')[0];
  return { defaultCreatedTo, defaultCreatedFrom };
};

/**
 * Returns true if any filter value is selected (non-empty).
 * @param {object} values - Filter values object
 * @param {object} options - { excludeWhen: (key, value) => boolean } - return true to exclude a key from the check
 */
export const hasAnyFilterSelected = (values, options = {}) => {
  if (!values || typeof values !== 'object') return false;
  const { excludeWhen } = options;
  return Object.entries(values).some(([key, value]) => {
    if (excludeWhen && excludeWhen(key, value)) return false;
    if (value == null) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return true;
  });
};

/**
 * Minimum number of characters required before a free-text filter field can be applied.
 * Keyed by field name so it works across the NeeNor, Business Partner and Customer
 * Application filter forms (EORI is named `eori` on some forms and `eoriNumber` on others).
 */
export const FILTER_MIN_LENGTHS = {
  companyName: 3,
  eori: 2,
  eoriNumber: 2,
};

/**
 * Returns a validation message when a free-text filter field has a value that is
 * non-empty but shorter than its minimum length, otherwise null.
 */
export const getFilterMinLengthError = (field, value) => {
  const min = FILTER_MIN_LENGTHS[field];
  if (!min) return null;
  const trimmed = (value ?? '').toString().trim();
  if (trimmed.length > 0 && trimmed.length < min) {
    return `Enter at least ${min} characters`;
  }
  return null;
};

/** Returns true if any free-text filter field has a value below its minimum length. */
export const hasFilterMinLengthErrors = (values) =>
  Object.keys(FILTER_MIN_LENGTHS).some((field) => getFilterMinLengthError(field, values?.[field]));

/** Terminal load statuses: results must include those rows; do not apply "exclude completed & cancelled". */
export const shouldUncheckExcludeCompletedForStatus = (statusLabel) => {
  if (statusLabel == null) return false;
  const s = String(statusLabel).trim().toLowerCase();
  return s === 'completed' || s === 'cancelled' || s === 'canceled';
};

/**
 * FilterableSelect stores option `id` (e.g. EPO statusNumber), not the display `data` string.
 * Map id → data for exclude-completed logic.
 * @param {unknown} raw - value from filter state (id or label)
 * @param {{ id?: unknown, data?: string }[]} statusOptions - from fetchDropdownOptions statuses
 */
export const resolveStatusDataLabel = (raw, statusOptions = []) => {
  if (raw == null) return null;
  const opt = statusOptions.find((o) => o?.id?.toString?.() === raw?.toString?.());
  if (opt?.data != null && String(opt.data).trim() !== '') return opt.data;
  return raw;
};
